/**
 * Helper functions for time filtering functionality
 * These functions are specifically for handling time input and filtering in the TimeFilterPicker component
 */

/**
 * Helper function to convert 12-hour time to 24-hour format
 * @param time12Hour - Time string in 12-hour format (H:MM AM/PM)
 * @returns Time string in 24-hour format (HH:MM)
 */
export const convert12HourTo24Hour = (time12Hour: string): string => {
    const match = time12Hour.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return time12Hour;
    
    let hours = parseInt(match[1]);
    const minutes = match[2];
    const period = match[3].toUpperCase();
    
    if (period === 'AM' && hours === 12) {
        hours = 0;
    } else if (period === 'PM' && hours !== 12) {
        hours += 12;
    }
    
    return `${hours.toString().padStart(2, '0')}:${minutes}`;
};

/**
 * Helper function to normalize time string for comparison
 * @param timeString - Time string in any format
 * @returns Normalized time string for comparison
 */
export const normalizeTimeForComparison = (timeString: string): string => {
    // If it's already in 12-hour format, convert to 24-hour for comparison
    if (timeString.match(/^\d{1,2}:\d{2}\s*(AM|PM)$/i)) {
        return convert12HourTo24Hour(timeString);
    }
    // If it's in 24-hour format, return as is
    if (timeString.match(/^\d{2}:\d{2}$/)) {
        return timeString;
    }
    return timeString;
};

/**
 * Helper function to format time for display in messages
 * @param timeString - Time string in any format
 * @returns Formatted time string for display (12H format)
 */
export const formatTimeForDisplay = (timeString: string): string => {
    // If it's already in 12-hour format, return as is
    if (timeString.match(/^\d{1,2}:\d{2}\s*(AM|PM)$/i)) {
        return timeString;
    }
    // If it's in 24-hour format, convert to 12-hour
    if (timeString.match(/^\d{2}:\d{2}$/)) {
        const [hours, minutes] = timeString.split(':').map(Number);
        return formatTime12Hour(hours, minutes);
    }
    return timeString;
};

/**
 * Helper function to convert 24-hour time to 12-hour format with AM/PM
 * @param hours - Hours in 24-hour format (0-23)
 * @param minutes - Minutes (0-59)
 * @returns Formatted time string in 12-hour format (HH:MM AM/PM)
 */
const formatTime12Hour = (hours: number, minutes: number): string => {
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${displayHours}:${displayMinutes} ${period}`;
};
